
// Global helper functions for accessibility
function getFocusableElements(container) {
  const focusable = Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );

  return focusable.filter((el) => !el.closest('.trap-focus-exclude'));
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class TextMarquee extends HTMLElement {
  constructor() {
    super();
    this.isTicking = false;
    this.lastKnownY = 0;
    this._resizeObserver = null;
    this.isThemeEditor = window.Shopify && window.Shopify.designMode;

    this.autoOffsetX = 0;
    this.autoSpeed = 1;
    this.lastFrameTime = null;
    this.isUserScrolling = false;
    this.scrollIdleTimeout = null;

    this.onScroll = this.onScroll.bind(this);
    this.requestTick = this.requestTick.bind(this);
    this.updateEl = this.updateEl.bind(this);
    this.moveContainer = this.moveContainer.bind(this);
    this.setupTickerSpeed = this.setupTickerSpeed.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
  }

  connectedCallback() {
    if (this.isThemeEditor) {
      return;
    }

    this._els = {
      container: this.querySelector('[data-ticker-el="container"]'),
      message: this.querySelector('[data-ticker-el="message"]'),
    };

    if (!this._els.container || !this._els.message) {
      console.warn('TextMarquee: missing required children with data-ticker-el="container" and "message".');
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '100px 0px 100px 0px'
    });
    this.observer.observe(this);

    this.lastKnownY = window.scrollY;
    this.isVisible = false;

    this.setupTickerSpeed();

    this._resizeObserver = new ResizeObserver(() => {
      if (this.isVisible) {
        this.setupTickerSpeed();
        this.requestTick();
      }
    });
    this._resizeObserver.observe(this._els.message);
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.isVisible = false;
    this.isTicking = false;
    this.lastFrameTime = null;
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.isVisible = true;
        this.addEventListeners();
        this.requestTick();
      } else {
        this.isVisible = false;
        this.removeEventListeners();
      }
    });
  }

  addEventListeners() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.scrollIdleTimeout) {
      clearTimeout(this.scrollIdleTimeout);
      this.scrollIdleTimeout = null;
    }
    this.isUserScrolling = false;
  }

  getOffsetTop(el) {
    let offsetTop = 0;
    let node = el;
    while (node && node !== document.body) {
      offsetTop += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return offsetTop;
  }

  setupTickerSpeed() {
    const messageWidth = this._els.message.offsetWidth || 0;
    const isSmall = window.matchMedia('(max-width: 767px)').matches;
    const speedFactor = isSmall ? 120 : 80;
    const tickerSpeed = messageWidth > 0 ? messageWidth / speedFactor : 30;

    this.style.setProperty('--ticker-speed', `${tickerSpeed}s`);

    this.autoSpeed = 100 / (tickerSpeed * 1.5);
  }

  onScroll() {
    if (!this.isVisible) return;

    this.lastKnownY = window.scrollY;

    this.isUserScrolling = true;
    if (this.scrollIdleTimeout) {
      clearTimeout(this.scrollIdleTimeout);
    }
    this.scrollIdleTimeout = setTimeout(() => {
      this.isUserScrolling = false;
    }, 150);
  }

  requestTick() {
    if (this.isTicking || !this.isVisible) return;
    this.isTicking = true;
    this.lastFrameTime = null;
    requestAnimationFrame(this.updateEl);
  }

  updateEl(timestamp) {
    if (!this.isVisible) {
      this.isTicking = false;
      this.lastFrameTime = null;
      return;
    }

    if (this.lastFrameTime == null) {
      this.lastFrameTime = timestamp;
    }
    const delta = (timestamp - this.lastFrameTime) / 1000;
    this.lastFrameTime = timestamp;

    if (!this.isUserScrolling) {
      this.autoOffsetX += this.autoSpeed * delta;
      if (this.autoOffsetX > 30) {
        this.autoOffsetX -= 30;
      }
    } else {
      if (this.autoOffsetX !== 0) {
        const decay = Math.min(Math.abs(this.autoOffsetX), this.autoSpeed * delta * 4);
        this.autoOffsetX += this.autoOffsetX > 0 ? -decay : decay;
      }
    }

    this.moveContainer();

    if (this.isVisible) {
      requestAnimationFrame(this.updateEl);
    } else {
      this.isTicking = false;
      this.lastFrameTime = null;
    }
  }

  moveContainer() {
    if (!this.offsetHeight || !this.isVisible) {
      return;
    }

    const amountToMoveX = 100;
    const scrollSlowdownFactor = 5;
    const elementHeight = this.offsetHeight;

    const elementStartPoint = this.getOffsetTop(this) - window.innerHeight;
    const elementEndPoint = this.getOffsetTop(this) + elementHeight;

    if (this.lastKnownY <= elementStartPoint) {
      this._els.container.style.transform = 'translate3d(0%, 0px, 0px)';
      return;
    }

    if (this.lastKnownY > elementEndPoint) {
      this._els.container.style.transform = 'translate3d(-100%, 0px, 0px)';
      return;
    }

    const totalScrollAmount = (elementEndPoint - elementStartPoint) * scrollSlowdownFactor;
    const scrollYAsAPercentageOfJourney = Math.max(
      0,
      Math.min(
        1,
        (this.lastKnownY - elementStartPoint) / totalScrollAmount
      )
    );

    const scrollBaseX = (amountToMoveX * scrollYAsAPercentageOfJourney)
    let totalX = scrollBaseX + this.autoOffsetX;

    totalX = Math.max(0, Math.min(100, totalX));

    this._els.container.style.transform = `translate3d(-${totalX}%, 0px, 0px)`;
  }
}

if (!customElements.get('text-marquee')) {
  customElements.define('text-marquee', TextMarquee);
}

document.addEventListener('shopify:section:load', function(event) {
  const textMarquees = event.target.querySelectorAll('text-marquee');
  textMarquees.forEach(marquee => {
    if (marquee.connectedCallback) {
      marquee.connectedCallback();
    }
  });
});

document.addEventListener('shopify:section:unload', function(event) {
  const textMarquees = event.target.querySelectorAll('text-marquee');
  textMarquees.forEach(marquee => {
    if (marquee.disconnectedCallback) {
      marquee.disconnectedCallback();
    }
  });
});

document.addEventListener('shopify:block:select', function(event) {
  const textMarquee = event.target.closest('text-marquee');
  if (textMarquee && textMarquee._els && textMarquee._els.container) {
    textMarquee._els.container.style.transform = 'translate3d(-15%, 0px, 0px)';
  }
});




// Quick Add Modal Component
class QuickAddModal extends HTMLElement {
  constructor() {
    super();
    this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    this.isLoading = false;
    this.cache = new Map();
    this.cartDrawerObserver = null;
    this.originalParentElement = null;
    this.cartUpdateUnsubscriber = null;
    this.xrRetryTimeouts = [];
    this.xrRetryInterval = null;
  }

  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('quick-add-modal__toggle') || 
          event.target.closest('.quick-add-modal__toggle')) {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      }
    });
    
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.hide();
      }
    });
    
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.hide();
      }
    });

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this.cartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        this.cache.clear();
        if (this.hasAttribute('open')) {
          this.hide(true);
        }
      });
    }

    this.setupCartDrawerObserver();
  }

  showModal(opener) {
    this.openedBy = opener;
    this.focusElement = document.activeElement;
    
    if (!this.originalParentElement && this.parentElement && this.parentElement !== document.body) {
      this.originalParentElement = this.parentElement;
    }
    
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
    
    this.setAttribute('open', '');
    document.body.classList.add('modal-open');
    this.refreshMediaCarousels();
    
    const closeButton = this.querySelector('.quick-add-modal__toggle');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      });
    }
      
    this.focusTrap();
    
    this.announce();
  }

  refreshMediaCarousels() {
    const refresh = () => {
      const carousels = this.querySelectorAll('product-media-carousel');
      carousels.forEach((carousel) => {
        if (typeof carousel.initDomRefs === 'function') {
          carousel.initDomRefs();
        }

        if (carousel.thumbnailSlider) {
          carousel.thumbnailSlider.update();
          carousel.thumbnailSlider.navigation?.update();
          if (typeof carousel.updateThumbnailNavigation === 'function') {
            carousel.updateThumbnailNavigation(carousel.thumbnailSlider);
          }
        }

        if (carousel.mainSlider) {
          carousel.mainSlider.update();
          carousel.mainSlider.navigation?.update();
          if (typeof carousel.normalizeHeight === 'function') {
            carousel.normalizeHeight(carousel.mainSlider);
          }
        }

        if (typeof carousel.updateThumbnailAccessibility === 'function') {
          carousel.updateThumbnailAccessibility();
        }
      });
    };

    requestAnimationFrame(refresh);
    window.setTimeout(refresh, 120);
  }

  hide(preventFocus = false) {
    const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cartNotification) cartNotification.classList.remove('is-empty');

    if (preventFocus) {
      this.openedBy = null;
      this.focusElement = null;
    }
    
    this.removeAttribute('open');
    document.body.classList.remove('modal-open');
    
    if (!preventFocus && this.openedBy) {
      this.openedBy.focus();
    } else if (!preventFocus && this.focusElement) {
      this.focusElement.focus();
    }
    
    if (this.originalParentElement && document.contains(this.originalParentElement)) {
      this.originalParentElement.appendChild(this);
    } else if (this.parentElement === document.body) {
      this.remove();
    }

    this.clearXrRetryJobs();
  }

  setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }

    this.cartDrawerObserver = new MutationObserver(() => {
      if (!this.hasAttribute('open')) return;
      if (cartDrawer.classList.contains('is-open')) {
        this.hide(true);
      }
    });

    this.cartDrawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    this.clearXrRetryJobs();

    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
      this.cartDrawerObserver = null;
    }
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = null;
  }

  clearXrRetryJobs() {
    this.xrRetryTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.xrRetryTimeouts = [];

    if (this.xrRetryInterval) {
      window.clearInterval(this.xrRetryInterval);
      this.xrRetryInterval = null;
    }
  }

  setupShopifyXRWithRetries(container) {
    if (!container) {
      return;
    }

    const hasXrElements = container.querySelector('[data-shopify-xr], .product__xr-button, product-model');
    if (!hasXrElements) {
      return;
    }

    this.clearXrRetryJobs();

    const runXRSetup = () => {
      if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
        window.ProductModel.loadShopifyXR();
      }

      if (window.ShopifyXR && typeof window.ShopifyXR.setupXRElements === 'function') {
        window.ShopifyXR.setupXRElements();
      }
    };

    runXRSetup();

    [120, 350, 700].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        runXRSetup();
      }, delay);
      this.xrRetryTimeouts.push(timeoutId);
    });

    let attempts = 0;
    const maxAttempts = 12;
    this.xrRetryInterval = window.setInterval(() => {
      const hiddenButtons = container.querySelectorAll('.product__xr-button[data-shopify-xr-hidden]');
      if (!hiddenButtons.length) {
        this.clearXrRetryJobs();
        return;
      }

      attempts += 1;
      runXRSetup();

      if (attempts >= maxAttempts) {
        this.clearXrRetryJobs();
      }
    }, 160);
  }

  show(opener) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    opener.setAttribute('aria-disabled', true);
    opener.classList.add('loading');
    
    const loadingSpinner = opener.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.remove('hidden');

    const productUrl = opener.getAttribute('data-product-url');
    if (!productUrl) {
      console.error('No product URL found for quick add');
      this.resetButton(opener);
      return;
    }

    if (this.cache.has(productUrl)) {
      this.renderProductHTML(this.cache.get(productUrl));
      this.showModal(opener);
      this.resetButton(opener);
      this.isLoading = false;
      return;
    }

    fetch(productUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        
        let productElement = responseHTML.querySelector('product-info') || 
                           responseHTML.querySelector('[data-product-form]') ||
                           responseHTML.querySelector('.product-form') ||
                           responseHTML.querySelector('form[action*="/cart/add"]') ||
                           responseHTML.querySelector('.product');

        if (!productElement) {
          productElement = responseHTML.querySelector('form');
        }

        if (!productElement) {
          console.error('Quick Add: No product element found. Available elements:', {
            productInfo: responseHTML.querySelector('product-info'),
            productForm: responseHTML.querySelector('[data-product-form]'),
            productFormClass: responseHTML.querySelector('.product-form'),
            cartForm: responseHTML.querySelector('form[action*="/cart/add"]'),
            product: responseHTML.querySelector('.product'),
            anyForm: responseHTML.querySelector('form'),
            body: responseHTML.body?.innerHTML?.substring(0, 200)
          });
          
          const fallbackForm = this.createFallbackForm(productUrl);
          if (fallbackForm) {
            productElement = fallbackForm;
          } else {
            throw new Error('Product info not found in response');
          }
        }

        this.preprocessHTML(productElement);
        const productHTML = productElement.outerHTML;
        
        this.cache.set(productUrl, productHTML);
        this.renderProductHTML(productHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        this.showModal(opener);
      })
      .catch((error) => {
        console.error('Quick add error:', error);
        this.showError();
      })
      .finally(() => {
        this.resetButton(opener);
        this.isLoading = false;
      });
  }

  resetButton(opener) {
    opener.removeAttribute('aria-disabled');
    opener.classList.remove('loading');
    const loadingSpinner = opener.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.add('hidden');
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Sorry, there was an error loading this product. Please try again.</p>
        <button type="button" class="button quick-add-modal__toggle">
          Close
        </button>
      </div>
    `;
    
    if (this.openedBy) {
      this.setAttribute('open', '');
      document.body.classList.add('modal-open');
      
      const errorCloseButton = this.querySelector('.quick-add-modal__toggle');
      if (errorCloseButton) {
        errorCloseButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        });
      }
    }
  }

  createFallbackForm(productUrl) {
    const form = document.createElement('form');
    form.action = '/cart/add';
    form.method = 'post';
    form.className = 'product-form';
    
    const title = document.createElement('h3');
    title.textContent = 'Add to Cart';
    form.appendChild(title);
    
    const quantityLabel = document.createElement('label');
    quantityLabel.textContent = 'Quantity:';
    const quantityInput = document.createElement('input');
    quantityInput.type = 'number';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';
    quantityInput.min = '1';
    form.appendChild(quantityLabel);
    form.appendChild(quantityInput);
    
    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.textContent = 'Add to Cart';
    submitButton.className = 'button button--primary';
    form.appendChild(submitButton);
    
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.textContent = 'Close';
    closeButton.className = 'button quick-add-modal__toggle';
    form.appendChild(closeButton);
    
    return form;
  }

  focusTrap() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement) firstElement.focus();

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    });
  }

  announce() {
    const announcement = this.querySelector('[aria-label]');
    if (announcement) {
      const announcementText = announcement.getAttribute('aria-label');
      const announcementElement = document.createElement('div');
      announcementElement.setAttribute('aria-live', 'polite');
      announcementElement.setAttribute('aria-atomic', 'true');
      announcementElement.className = 'visually-hidden';
      announcementElement.textContent = announcementText;
      document.body.appendChild(announcementElement);
      
      setTimeout(() => {
        document.body.removeChild(announcementElement);
      }, 1000);
    }
  }

  preprocessHTML(productElement) {
    const quickAddButtons = productElement.querySelectorAll('.quick-add');
    quickAddButtons.forEach(button => button.remove());

    const quickAddHiddenElements = productElement.querySelectorAll('.quick-add-hidden');
    quickAddHiddenElements.forEach((element) => element.remove());

    productElement.setAttribute('data-update-url', 'false');

    const variantPickers = productElement.querySelectorAll('variant-picker');
    variantPickers.forEach((picker) => {
      picker.setAttribute('data-update-url', 'false');
    });

    const forms = productElement.querySelectorAll('form');
    forms.forEach((form, index) => {
      const oldId = form.id || `form-${index}`;
      const newId = `quick-add-form-${Date.now()}-${index}`;
      form.id = newId;

      productElement.querySelectorAll(`[form="${oldId}"]`).forEach((control) => {
        if (control.name === 'quantity') {
          control.removeAttribute('form');
        } else {
          control.setAttribute('form', newId);
        }
      });

      const inputs = form.querySelectorAll('input, select, textarea');
      inputs.forEach(input => {
        if (input.name === 'id') {
          input.id = `${newId}-variant-id`;
        }
      });
    });
  }

  renderProductHTML(productHTML) {
    if (!productHTML) {
      this.modalContent.innerHTML = '';
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = productHTML;
    const fragment = template.content;

    fragment.querySelectorAll('script').forEach((oldScript) => {
      const src = oldScript.getAttribute('src');
      const shouldSkip = src && (
        (src.includes('component-deferred-media.js') && customElements.get('deferred-media')) ||
        (src.includes('component-product-model.js') && customElements.get('product-model')) ||
        (src.includes('product-model.js') && customElements.get('product-model'))
      );

      if (shouldSkip) {
        oldScript.remove();
        return;
      }

      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => {
        newScript.setAttribute(attribute.name, attribute.value);
      });
      newScript.append(oldScript.textContent || '');
      oldScript.replaceWith(newScript);
    });

    this.modalContent.innerHTML = '';
    this.modalContent.appendChild(fragment);

    this.hydrateProductInfo(this.modalContent);
  }

  hydrateProductInfo(container) {
    if (!container) {
      return;
    }

    requestAnimationFrame(() => {
      const productInfo = container.querySelector('product-info') || container;
      const carousels = Array.from(productInfo.querySelectorAll('product-media-carousel'));

      const initializeCarousels = () => {
        carousels.forEach((carousel) => {
          if (typeof carousel.destroyAll === 'function') {
            carousel.destroyAll();
          }

          if (typeof carousel.initDomRefs === 'function') {
            carousel.initDomRefs();
          }

          if (typeof carousel.applyMode === 'function') {
            carousel.applyMode();
          }

          const shouldHaveSlider =
            typeof carousel.getViewportMode === 'function' &&
            carousel.getViewportMode() === 'slider';

          if (
            shouldHaveSlider &&
            typeof carousel.initSliders === 'function' &&
            !carousel.mainSlider
          ) {
            carousel.initSliders();
          }
        });
      };

      if (carousels.length) {
        if (customElements.get('product-media-carousel')) {
          initializeCarousels();
        } else {
          customElements.whenDefined('product-media-carousel').then(() => initializeCarousels());
        }
      }

      this.setupShopifyXRWithRetries(productInfo);

      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('product-media:changed', { bubbles: true }));

      const loadedEvent = new CustomEvent('product-info:loaded', {
        bubbles: true,
        detail: { productInfo }
      });
      productInfo.dispatchEvent(loadedEvent);
    });
  }
}
if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', QuickAddModal);
}

// Modal Dialog Base Class
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.openedBy = null;
    this.focusElement = null;
  }

  connectedCallback() {
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.hide();
  }

  onClick(event) {
    if (event.target !== this) return;
    this.hide();
  }

  show(opener) {
    this.openedBy = opener;
    this.focusElement = document.activeElement;
    this.setAttribute('open', '');
    document.body.classList.add('modal-open');
    
    const closeButton = this.querySelector('.quick-add-modal__toggle');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      });
    }
    
    this.focusTrap();
    
    this.announce();
  }

  hide() {
    this.removeAttribute('open');
    document.body.classList.remove('modal-open');
    
    if (this.openedBy) {
      this.openedBy.focus();
    } else if (this.focusElement) {
      this.focusElement.focus();
    }
  }

  focusTrap() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement) firstElement.focus();

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    });
  }

  announce() {
    const announcement = this.querySelector('[aria-label]');
    if (announcement) {
      const announcementText = announcement.getAttribute('aria-label');
      const announcementElement = document.createElement('div');
      announcementElement.setAttribute('aria-live', 'polite');
      announcementElement.setAttribute('aria-atomic', 'true');
      announcementElement.className = 'visually-hidden';
      announcementElement.textContent = announcementText;
      document.body.appendChild(announcementElement);
      
      setTimeout(() => {
        document.body.removeChild(announcementElement);
      }, 1000);
    }
  }
}
if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}

// Modal Opener Component
class ModalOpener extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
  }

  connectedCallback() {
    this.button.addEventListener('click', this.openModal.bind(this));
  }

  openModal(event) {
    event.preventDefault();
    const modalId = this.getAttribute('data-modal');
    const modal = document.querySelector(modalId);
    
    if (modal) {
      modal.show(this.button);
    }
  }
}
if (!customElements.get('modal-opener')) {
  customElements.define('modal-opener', ModalOpener);
}



class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    const hasExistingContent = this.querySelector('.grid__item, .swiper-slide, .card-product, .featured-collection-container');
    const shouldFetchOnLoad = Boolean(this.dataset.intent);
    
    if (hasExistingContent && !shouldFetchOnLoad) {
      this.classList.add('product-recommendations--loaded');
      const sectionLayout = this.closest('.section-layout:not(.cart-content-layout)');
      if (sectionLayout) {
        sectionLayout.style.display = '';
      }
      this.initializeSwiper();
    } else {
      this.style.display = 'none';
      const sectionLayout = this.closest('.section-layout:not(.cart-content-layout)');
      if (sectionLayout) {
        sectionLayout.style.display = 'none';
      }
      this.initializeRecommendations(this.dataset.productId);
    }
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    
    this.loadRecommendations(productId);
    
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    if (this.hasAttribute('data-loading')) {
      return;
    }
    
    this.setAttribute('data-loading', 'true');
    
    this.loadComplementaryProducts(productId)
      .then((hasContent) => {
        if (hasContent) {
          this.classList.add('product-recommendations--loaded');
          this.initializeSwiper();
          this.style.display = '';
          const sectionLayout = this.closest('.section-layout:not(.cart-content-layout)');
          if (sectionLayout) {
            sectionLayout.style.display = '';
          }
          this.removeAttribute('data-loading');
          return;
        }
        
        this.style.display = 'none';
        const sectionLayout = this.closest('.section-layout:not(.cart-content-layout)');
        if (sectionLayout) {
          sectionLayout.style.display = 'none';
        }
        this.removeAttribute('data-loading');
      })
      .catch((error) => {
        console.error('Failed to load complementary products:', error);
        this.style.display = 'none';
        const sectionLayout = this.closest('.section-layout:not(.cart-content-layout)');
        if (sectionLayout) {
          sectionLayout.style.display = 'none';
        }
        this.removeAttribute('data-loading');
      });
  }

  async loadComplementaryProducts(productId) {
    const baseUrl = this.dataset.url;
    
    if (!baseUrl) {
      console.error('ProductRecommendations: Missing required data-url attribute');
      return false;
    }

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', this.dataset.sectionId);

    if (this.dataset.intent) {
      url.searchParams.set('intent', this.dataset.intent);
    }
    
    try {
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('product-recommendations');

      if (recommendations) {
        const hasProducts = recommendations.querySelector('.grid__item, .swiper-slide, .card-product');
        const hasContent = recommendations.innerHTML.trim().length > 0;
        
        if (hasProducts || (hasContent && recommendations.querySelector('.featured-collection-container, .grid'))) {
          this.innerHTML = recommendations.innerHTML;
          
          // Wait for custom elements to initialize before dispatching event
          // Use setTimeout to ensure DOM is fully updated and custom elements are connected
          setTimeout(() => {
            this.dispatchEvent(new CustomEvent('product-recommendations:loaded', {
              bubbles: true,
              detail: { element: this }
            }));
          }, 100);
          
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Failed to load complementary products:', error);
      return false;
    }
  }


  getBlockSettings(block) {
    return {
      heading: this.dataset.heading || 'You might also like',
      heading_size: this.dataset.headingSize || 'h3',
      cols_mobile: parseInt(this.dataset.colsMobile) || 1,
      cols_desktop: parseInt(this.dataset.colsDesktop) || 3,
      show_vendor: this.dataset.showVendor === 'true',
      show_type: this.dataset.showType === 'true',
      show_price: this.dataset.showPrice !== 'false'
    };
  }


  initializeSwiper() {
    const swiperSlider = this.querySelector('swiper-slider');
    if (swiperSlider) {
      // Swiper will auto-initialize via the swiper-slider custom element
      // No additional initialization needed
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

document.addEventListener('shopify:section:load', (event) => {
  const complementaryProducts = event.target.querySelector('product-recommendations');
  if (complementaryProducts) {
    complementaryProducts.initializeRecommendations(complementaryProducts.dataset.productId);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const complementaryProducts = event.target.querySelector('product-recommendations');
  if (complementaryProducts && complementaryProducts.observer) {
    complementaryProducts.observer.unobserve(complementaryProducts);
  }
});

class ScrollToTop extends HTMLElement {
  constructor() {
    super();
    this._btn = null;
    this._visible = false;
    this._isNearBottom = false;
    this._onScroll = this._onScroll.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._btn = this.querySelector('button');
    if (!this._btn) return;

    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._btn.addEventListener('click', this._onClick);

    this._onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    if (this._btn) this._btn.removeEventListener('click', this._onClick);
  }

  _onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const scrolled = (scrollTop + winHeight) / docHeight * 100;
    const shouldBeVisible = scrolled >= 40;
    const distanceToBottom = docHeight - (scrollTop + winHeight);
    const isNearBottom = distanceToBottom <= 75;
    
    if (shouldBeVisible !== this._visible) {
      this.classList.toggle('active', shouldBeVisible);
      this._visible = shouldBeVisible;
    }
    
    if (isNearBottom !== this._isNearBottom) {
      this.classList.toggle('scroll-stop', isNearBottom);
      this._isNearBottom = isNearBottom;
    }
  }

  _onClick() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

customElements.define('scroll-to-top', ScrollToTop);



class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon') || this.querySelector('.svg-wrapper');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);


// Pub/Sub system for component communication
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

// Event constants
const PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};
window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;

// Debounce function (from theme-dawn)
function debounce(fn, wait, immediate = false) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => {
      if (!immediate) fn.apply(this, args);
    }, wait);
    if (immediate && !t) {
      fn.apply(this, args);
    }
  };
}

// Fetch config helper
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

// Routes object (if not defined by Shopify)
if (typeof window.routes === 'undefined') {
  window.routes = {
    cart_url: `${window.Shopify?.routes?.root || '/'}cart`,
    cart_update_url: `${window.Shopify?.routes?.root || '/'}cart/update.js`,
    cart_change_url: `${window.Shopify?.routes?.root || '/'}cart/change.js`,
    predictive_search_url: '/search/suggest',
  };
}


class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}



console.log('Styra 1.0.0');